import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Paperclip, Link2, X, CheckCheck } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import AudienceGate from "../files/AudienceGate";
import { useMe } from "@/lib/rehearsalRoomQueries";
import { useThreadMessages, useSaveDraft, useSendMessage, mqk } from "@/lib/messagingQueries";
import { useFileIndex } from "@/lib/fileQueries";

const DRAFT_SAVE_DEBOUNCE_MS = 900;

/**
 * Master v5 Prompt 13 — extracted (Prompt 19) so the same thread
 * view/composer/AudienceGate flow renders identically inside a single
 * Workspace's MessagesPage.jsx AND inside the coach's cross-Workspace
 * CoachMessagesWorkbenchPage.jsx — one implementation, not two drifting
 * copies of "every Team send runs a fresh AudienceGate."
 */
export function ThreadView({ workspaceId, threadId, isTeamWorkspace, actorRole, teamMembers, onSentToThread, lockedAudienceSelection }) {
  const { data, isPending } = useThreadMessages(workspaceId, threadId);
  const saveDraft = useSaveDraft(workspaceId, threadId);
  const sendMessage = useSendMessage(workspaceId);
  const { data: fileIndex } = useFileIndex(workspaceId);
  const { data: me } = useMe();
  const qc = useQueryClient();

  const [bodyText, setBodyText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [contextLink, setContextLink] = useState(null);
  const [audienceSelection, setAudienceSelection] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [gateKey, setGateKey] = useState(0);
  const hydratedThreadIdRef = useRef(null);
  const scrollRef = useRef(null);

  // Fix (testing agent, iteration 57, MEDIUM): pin the message list to
  // its newest message on every open/append — a fixed-height scroll
  // container otherwise renders from the top, hiding a just-sent
  // message below the fold.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [threadId, data?.messages?.length]);

  useEffect(() => {
    if (!data || hydratedThreadIdRef.current === threadId) return;
    hydratedThreadIdRef.current = threadId;
    if (data.draft) {
      setBodyText(data.draft.body_text || "");
      setAttachments(data.draft.attachments || []);
      setContextLink(data.draft.context_link || null);
    } else {
      setBodyText(""); setAttachments([]); setContextLink(null);
    }
    setAudienceSelection(null);
    setGateKey((k) => k + 1);
  }, [threadId, data]);

  useEffect(() => {
    if (!data) return;
    qc.invalidateQueries({ queryKey: mqk.threads(workspaceId) });
    qc.invalidateQueries({ queryKey: ["notification-unread-count", workspaceId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, data?.messages?.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bodyText || attachments.length || contextLink) {
        saveDraft.mutate({ body_text: bodyText, attachments, context_link: contextLink });
      }
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyText, attachments, contextLink, threadId]);

  const showAudienceGate = isTeamWorkspace && !lockedAudienceSelection;
  const canSend = bodyText.trim().length > 0 && (!showAudienceGate || !!audienceSelection);
  const ownFiles = (fileIndex?.items || []).map((e) => e.asset).filter((a) => a.status === "ready");

  async function handleSend() {
    try {
      const sent = await sendMessage.mutateAsync({
        body_text: bodyText.trim(),
        attachments: attachments.map((a) => ({ fas_id: a.fas_id, label: a.label })),
        context_link: contextLink,
        ...(isTeamWorkspace ? { audience_selection: lockedAudienceSelection || audienceSelection } : {}),
      });
      setBodyText(""); setAttachments([]); setContextLink(null); setAudienceSelection(null);
      setGateKey((k) => k + 1);
      if (sent.thread_id && sent.thread_id !== threadId) {
        onSentToThread?.(sent.thread_id);
        toast.success("Message sent to a different thread based on the audience you chose");
      } else {
        toast.success("Message sent");
      }
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't send that message");
    }
  }

  return (
    <div className="flex flex-col min-h-[420px]" data-testid="messages-thread-view">
      <div ref={scrollRef} className="flex-1 px-4 py-4 space-y-3 overflow-y-auto max-h-[420px]">
        {isPending ? (
          <p className="text-sm text-ink-soft">Loading…</p>
        ) : (data?.messages || []).length === 0 ? (
          <p className="text-sm text-ink-soft italic" data-testid="messages-thread-empty">No messages yet — say hello.</p>
        ) : (
          data.messages.map((m) => <MessageBubble key={m.message_id} message={m} isMine={m.author_person_id === me?.per_id} />)
        )}
      </div>

      <div className="border-t border-ink/10 p-3 space-y-2" data-testid="messages-composer">
        {(attachments.length > 0 || contextLink) && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <span key={a.fas_id} className="inline-flex items-center gap-1 text-xs bg-ink/5 rounded px-2 py-1" data-testid={`messages-composer-attachment-${a.fas_id}`}>
                {a.label}
                <button onClick={() => setAttachments((prev) => prev.filter((x) => x.fas_id !== a.fas_id))} aria-label="Remove attachment"><X size={12} /></button>
              </span>
            ))}
            {contextLink && (
              <span className="inline-flex items-center gap-1 text-xs bg-mustard/20 rounded px-2 py-1" data-testid="messages-composer-context-link">
                {contextLink.label || contextLink.target_type}
                <button onClick={() => setContextLink(null)} aria-label="Remove link"><X size={12} /></button>
              </span>
            )}
          </div>
        )}

        <Textarea
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          placeholder="Write a message…"
          rows={2}
          data-testid="messages-composer-input"
        />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-1">
            <Popover open={attachOpen} onOpenChange={setAttachOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="messages-composer-attach-btn"><Paperclip size={15} /></Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" data-testid="messages-composer-attach-popover">
                <p className="text-xs font-medium text-ink-soft mb-2">Attach one of your files</p>
                {ownFiles.length === 0 ? (
                  <p className="text-xs text-ink-soft italic">No files yet.</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {ownFiles.map((f) => (
                      <button
                        key={f.fas_id}
                        onClick={() => { setAttachments((prev) => prev.some((a) => a.fas_id === f.fas_id) ? prev : [...prev, { fas_id: f.fas_id, label: f.original_filename }]); setAttachOpen(false); }}
                        className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-ink/5 truncate"
                        data-testid={`messages-composer-attach-option-${f.fas_id}`}
                      >
                        {f.original_filename}
                      </button>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {showAudienceGate && !audienceSelection && (
              <span className="text-xs text-ink-soft italic sm:hidden">Choose who should see this before sending</span>
            )}
          </div>

          {showAudienceGate && !audienceSelection && (
            <span className="text-xs text-ink-soft italic hidden sm:inline">Choose who should see this before sending</span>
          )}
          <Button size="sm" onClick={handleSend} disabled={!canSend || sendMessage.isPending} data-testid="messages-composer-send-btn" className="self-end sm:self-auto">
            <Send size={14} className="mr-1.5" /> {sendMessage.isPending ? "Sending…" : "Send"}
          </Button>
        </div>

        {showAudienceGate && (
          <AudienceGate key={gateKey} actorRole={actorRole} teamMembers={teamMembers} actionType="message" onConfirm={setAudienceSelection} />
        )}
      </div>
    </div>
  );
}

export function MessageBubble({ message, isMine }) {
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`messages-bubble-${message.message_id}`}>
      <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isMine ? "bg-terracotta/15" : "bg-ink/[0.04]"}`}>
        {!isMine && message.author_display_name && (
          <p className="text-[11px] font-medium text-terracotta mb-0.5">{message.author_display_name}</p>
        )}
        <p className="text-sm text-ink whitespace-pre-wrap">{message.body_text}</p>
        {message.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {message.attachments.map((a) => (
              <span key={a.fas_id} className="text-xs bg-ink/10 rounded px-2 py-0.5">{a.label}</span>
            ))}
          </div>
        )}
        {message.context_link && (
          <p className="text-xs text-terracotta mt-1 flex items-center gap-1"><Link2 size={11} /> {message.context_link.label || message.context_link.target_type}</p>
        )}
        <p className="text-[11px] text-ink-soft mt-1 flex items-center gap-1 justify-end">
          {new Date(message.created_at).toLocaleString()}
          {isMine && message.seen && (
            <span className="inline-flex items-center gap-0.5 text-terracotta" data-testid={`messages-bubble-seen-${message.message_id}`}>
              <CheckCheck size={12} /> Seen
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
